import { useState } from 'react';
import { Crown, Check, CreditCard, Shield, Zap, Globe, Activity, X, Loader2 } from 'lucide-react';
import { premiumService } from '@/services/premiumService';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useGlobalToast } from '@/contexts/ToastContext';

interface PremiumUpgradeProps {
  onUpgrade?: (planId: string) => void;
  onClose?: () => void;
  showModal?: boolean;
}

export function PremiumUpgrade({ onUpgrade, onClose, showModal = false }: PremiumUpgradeProps) {
  const [selectedPlan, setSelectedPlan] = useState<string>('premium');
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const { upgradeToPremium } = useAuthStore();
  const { success, error } = useGlobalToast();

  const plans = premiumService.getPlans().filter(plan => plan.id !== 'basic');

  const handleUpgrade = async (planId: string) => {
    setIsProcessing(true);

    try {
      const paymentResult = await premiumService.processPayment(planId, 'card');

      if (paymentResult.success) {
        const upgradeResult = premiumService.upgradeToPremium(
          'current-provider-id',
          planId,
          billingCycle
        );

        if (upgradeResult.success) {
          upgradeToPremium(planId);
          success("Upgrade Successful", `You are now subscribed to the ${planId} plan.`);
          onUpgrade?.(planId);
        }
      }
    } catch (err) {
      console.error('Upgrade failed:', err);
      error("Upgrade Failed", "There was an issue processing your upgrade. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'premium': return <Crown className="w-6 h-6 text-amber-500" />;
      case 'enterprise': return <Zap className="w-6 h-6 text-primary-600" />;
      default: return <Shield className="w-6 h-6 text-gray-400" />;
    }
  };

  const getYearlyDiscount = (monthlyPrice: number) => {
    const yearlyPrice = monthlyPrice * 10;
    const savings = (monthlyPrice * 12) - yearlyPrice;
    return { yearlyPrice, savings };
  };

  const content = (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Upgrade Your Professional Toolkit</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Unlock advanced features, priority support, and enhanced visibility to grow your business.
        </p>

        <div className="flex items-center justify-center mt-8 gap-4">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>Monthly</span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${billingCycle === 'yearly' ? 'bg-primary-600' : 'bg-gray-200'}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${billingCycle === 'yearly' ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
            Yearly <span className="text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-0.5 rounded-full ml-1">Save 17%</span>
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {plans.map((plan) => {
          const { yearlyPrice } = getYearlyDiscount(plan.price);
          const displayPrice = billingCycle === 'yearly' ? yearlyPrice : plan.price;
          const isSelected = selectedPlan === plan.id;

          return (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative rounded-2xl border-2 p-8 transition-all cursor-pointer ${isSelected
                  ? 'border-primary-600 bg-primary-50/10 ring-1 ring-primary-600 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-primary-200 hover:shadow-md'
                }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 -mr-1 -mt-1">
                  <Badge variant="success">Recommended</Badge>
                </div>
              )}

              <div className="flex items-start justify-between mb-6">
                <div className="p-3 bg-gray-50 rounded-xl">
                  {getPlanIcon(plan.id)}
                </div>
                <div className="text-right">
                  <div className="flex items-baseline justify-end gap-1">
                    <span className="text-3xl font-bold text-gray-900">€{displayPrice}</span>
                    <span className="text-sm font-medium text-gray-500">/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2 capitalize">{plan.name}</h3>
              <p className="text-sm text-gray-500 mb-6 min-h-[40px]">
                {plan.id === 'premium' ? 'Perfect for growing businesses.' : 'For established professionals needing advanced tools.'}
              </p>

              <hr className="border-gray-100 mb-6" />

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={isSelected ? 'primary' : 'outline'}
                className="w-full justify-center"
                size="lg"
                onClick={() => handleUpgrade(plan.id)}
                disabled={isProcessing}
              >
                {isProcessing && isSelected ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {isSelected ? 'Upgrade to ' + plan.name : 'Select ' + plan.name}
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 text-center">
        <h3 className="text-base font-semibold text-gray-900 mb-6">Trusted by professionals across Ireland</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex flex-col items-center gap-2">
            <Shield className="w-5 h-5 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">Secure Payments</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Activity className="w-5 h-5 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">99.9% Uptime</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Globe className="w-5 h-5 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">Nationwide Reach</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Check className="w-5 h-5 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">Verified Badge</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (showModal) {
    return (
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 relative animate-in zoom-in-95 duration-200">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {content}
        </div>
      </div>
    );
  }

  return content;
}